import * as React from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { useTheme } from "@mui/material";
import { Location } from "../../types/location";
import MapPreview from "../../components/MapPreview";
import { GlassDrawer } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface LocationDrawerProps {
  data: Location;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function LocationDrawer(props: LocationDrawerProps) {
  const { data, open, setOpen } = props;
  const theme = useTheme();
  const siteNumberLabel = useUILabel("common.siteNumber");
  const addressLabel = useUILabel("common.address");
  const countryLabel = useUILabel("location.country");
  const contactNameLabel = useUILabel("location.contactName");
  const contactPhoneLabel = useUILabel("location.contactPhone");
  const contactEmailLabel = useUILabel("location.contactEmail");
  const nALabel = useUILabel("location.nA");
  const [lng, lat] = React.useMemo(
    () => data?.coordinate?.coordinates || [],
    [data]
  );

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpen(open);
    };

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      cardProps={{
        sx: {
          p: { xs: 1.5, sm: 2 },
        },
      }}
      contentProps={{
        role: "presentation",
        sx: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 0,
          py: 0,
        },
      }}
    >
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          mt: { xs: 0.5, sm: 1.5 },
        }}
      >
        <ClearIcon
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: theme.shadows[2],
            width: "100%",
            minHeight: { xs: 200, sm: 240 },
            flexShrink: 0,
            "& > div": {
              borderRadius: 2,
            },
          }}
        >
          <MapPreview lat={lat} lng={lng} />
        </Box>
        <List
          sx={{
            "& .MuiListItem-root": { px: 0 },
            bgcolor: "transparent",
          }}
        >
          <ListItem>
            <Typography variant="h5" sx={{ color: "#12903C" }}>{data?.name}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={siteNumberLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <AssignmentIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.site_number || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={addressLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.address_1
                      ? `${data?.address_1}, ${data?.city}, ${data?.state} ${data?.zip}`
                      : nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={countryLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <PublicIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.country?.name || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={contactNameLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <PersonIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.primary_contact_name || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={contactPhoneLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.primary_contact_phone_number || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
              primary={contactEmailLabel}
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <EmailIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {data?.primary_contact_email || nALabel}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        </List>
      </Box>
    </GlassDrawer>
  );
}